import { useState } from 'react';
import { useStoreInfo } from 'commons/hooks';
import { useTranslationWithPrefix, validateFile } from 'commons/utils';
import { logFailure, logFileSize } from 'commons/utils/documentUploader/logger';
import chargebackServiceBR from '../services/chargebackServiceBR';
import { uploadFileToS3BR } from '../utils/uploadFileToS3BR';

const ACCEPT = 'application/pdf';

interface UseChargebackDocumentUploaderBRProps {
  orderId: string;
  maxDocumentSize: number;
  onSuccess: (key: string, duration: number) => void;
  onFileError: (text: string) => void;
  onUploadError: () => void;
  onDelete: () => void;
}

export function useChargebackDocumentUploaderBR({
  orderId,
  maxDocumentSize,
  onSuccess,
  onFileError,
  onUploadError,
  onDelete,
}: UseChargebackDocumentUploaderBRProps) {
  const { t } = useTranslationWithPrefix('chargebackUpload');
  const { storeInfo } = useStoreInfo();

  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState('');

  const handleFileChange = async (file: File) => {
    setIsLoading(true);
    const startTime = performance.now();

    const validation = validateFile(file, maxDocumentSize, ACCEPT, {
      invalidType: t('errors.invalidType'),
      sizeLimit: t('errors.sizeLimit'),
    });
    if (!validation.isValid) {
      onFileError(validation.errorMessage!);
      setIsLoading(false);
      return;
    }

    logFileSize(file, 'chargeback-defense', 'useChargebackDocumentUploaderBR');
    onFileError('');
    setFileName(file.name);

    try {
      const { url } = await chargebackServiceBR.generateChargebackS3UploadUrl(
        orderId,
        file.type,
        storeInfo.id,
      );
      await uploadFileToS3BR(file, url);
      const newBlobUrl = URL.createObjectURL(file);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(newBlobUrl);

      const key = new URL(url).pathname.slice(1);
      onSuccess(key, performance.now() - startTime);
    } catch (error) {
      logFailure(file, 'chargeback-defense', error);
      onUploadError();
      setFileName(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileDelete = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl('');
    setFileName(null);
    onDelete();
  };

  return { isLoading, fileName, blobUrl, handleFileChange, handleFileDelete };
}
